import { useState, useEffect, useRef } from 'react'
import { useKnowledgeStore } from '@/stores/knowledge'
import { useAuthStore } from '@/stores/auth'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { MarkdownRenderer } from '@/components/ui/markdown-renderer'
import { MarkdownEditor, SimpleMarkdownEditor } from '@/components/ui/markdown-editor'
import { EnhancementPreview } from '@/components/knowledge/enhancement-preview'
import { useToast } from '@/hooks/use-toast'
import { Save, X, Plus, Trash2, CheckCircle, Send, Eye, Edit3, FileText, Code, ChevronDown, ChevronUp, Sparkles, ExternalLink } from 'lucide-react'

interface FAQEditorProps {
  faq?: any
  onSave?: (faq: any) => void
  onCancel?: () => void
}

export function FAQEditor({ faq, onSave, onCancel }: FAQEditorProps) {
  const { createFAQ, updateFAQ } = useKnowledgeStore()
  const { user } = useAuthStore()
  const { toast } = useToast()
  const justSavedRef = useRef(false) // Use ref instead of state to avoid re-renders
  const lastFaqIdRef = useRef<string | null>(null) // Track last FAQ ID to detect switches
  const formDataInitializedRef = useRef(false) // Track if formData has been initialized

  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    question_variations: [] as string[],
    category: 'general',
    problem_type: '',
    tags: [] as string[],
    status: 'draft',
    escalation_rule: 'self_solve' as 'self_solve' | 'mission_solar' | 'neoom',
    solution_content: {
      steps: [] as string[],
      images: [] as string[],
      links: [] as string[]
    },
    followup_questions: [] as string[]
  })

  const [newVariation, setNewVariation] = useState('')
  const [newTag, setNewTag] = useState('')
  const [newStep, setNewStep] = useState('')
  const [newLink, setNewLink] = useState('')
  const [newFollowupQuestion, setNewFollowupQuestion] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [editingStep, setEditingStep] = useState<number | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [solutionStepsOpen, setSolutionStepsOpen] = useState(false)
  const [showEnhancement, setShowEnhancement] = useState(false)
  const [sourceUrl, setSourceUrl] = useState<string | null>(null)
  const [sourceType, setSourceType] = useState<string | null>(null)
  const [hasEnhancedData, setHasEnhancedData] = useState(false) // Track if we have enhanced data

  // Initialize lastFaqIdRef on first mount
  useEffect(() => {
    if (faq?.id && !lastFaqIdRef.current) {
      lastFaqIdRef.current = faq.id
    }
  }, [])

  useEffect(() => {
    const isNewFaq = faq?.id !== lastFaqIdRef.current
    const isFirstLoad = !formDataInitializedRef.current && faq
    const shouldResetFormData = faq && !hasEnhancedData && !justSavedRef.current && (isNewFaq || isFirstLoad)


    // Reset justSaved flag when switching to a different FAQ
    if (isNewFaq && lastFaqIdRef.current !== null) {
      justSavedRef.current = false
    }

    // Update last FAQ ID
    if (faq?.id) {
      lastFaqIdRef.current = faq.id
    }

    // Only reset formData if we should
    if (shouldResetFormData) {

      formDataInitializedRef.current = true // Mark as initialized

      setFormData({
        question: faq.question || '',
        answer: faq.answer || '',
        question_variations: faq.question_variations || [],
        category: faq.category || 'general',
        problem_type: faq.problem_type || '',
        tags: faq.tags || [],
        status: faq.status || 'draft',
        escalation_rule: faq.escalation_rule || 'self_solve',
        solution_content: faq.solution_content || {
          steps: [],
          images: [],
          links: []
        },
        followup_questions: faq.followup_questions || []
      })

      // Load source URL if source_uid exists
      if (faq.source_uid) {
        loadSourceUrl(faq.source_uid)
      } else {
        setSourceUrl(null)
        setSourceType(null)
      }
    } else if (!faq) {
      // Only reset form for new FAQ when there's no FAQ at all
      setFormData({
        question: '',
        answer: '',
        question_variations: [],
        category: 'general',
        problem_type: '',
        tags: [],
        status: 'draft',
        escalation_rule: 'self_solve',
        solution_content: {
          steps: [],
          images: [],
          links: []
        },
        followup_questions: []
      })
      setSourceUrl(null)
      setSourceType(null)
    }
    // If faq exists but shouldResetFormData is false, do nothing - keep current form data
  }, [faq, hasEnhancedData]) // Removed justSaved from dependencies since it's now a ref

  const loadSourceUrl = async (sourceUid: string) => {
    try {
      const { data, error } = await supabase
        .from('import_sources')
        .select('source_url, source_type')
        .eq('uid', sourceUid)
        .single()

      if (error) {
        console.error('Error loading source URL:', error)
        return
      }

      if (data) {
        setSourceUrl(data.source_url)
        setSourceType(data.source_type)
      }
    } catch (error) {
      console.error('Error loading source URL:', error)
    }
  }

  // Cleanup states when component unmounts or cancels
  useEffect(() => {
    return () => {
      setNewVariation('')
      setNewTag('')
      setNewStep('')
      setNewLink('')
      setNewFollowupQuestion('')
      setIsSaving(false)
      setEditingStep(null)
      setShowAdvanced(false)
      setSolutionStepsOpen(false)
      setShowEnhancement(false)
      justSavedRef.current = false // Reset justSaved when component unmounts
      formDataInitializedRef.current = false // Reset initialization flag
      setHasEnhancedData(false) // Reset hasEnhancedData when component unmounts
    }
  }, [])

  const handleSubmit = async (status?: string) => {

    if (!formData.question || !formData.answer) {
      toast({
        title: 'Fehler',
        description: 'Frage und Antwort sind erforderlich',
        variant: 'destructive'
      })
      return
    }

    if (isSaving) {
      return // Prevent double-submit
    }

    setIsSaving(true)
    try {
      const { followup_questions, ...formDataWithoutFollowup } = formData
      const dataToSave = {
        ...formDataWithoutFollowup,
        status: status || formData.status,
        approved_by: status === 'approved' ? user?.email : null,
        approved_at: status === 'approved' ? new Date().toISOString() : null,
        // Include escalation_rule and followup_question_ids
        escalation_rule: formData.escalation_rule,
        // Only include followup_question_ids for mission_solar and neoom
        followup_question_ids: (formData.escalation_rule === 'mission_solar' || formData.escalation_rule === 'neoom')
          ? formData.followup_questions
          : []
      }


      if (faq?.id) {

        // Check authentication status first
        const { data: { user }, error: authError } = await supabase.auth.getUser()

        if (!user) {
          throw new Error('Nicht authentifiziert - bitte neu anmelden')
        }

        // Method 1: Try UPDATE with explicit auth headers
        const { error: saveError } = await supabase
          .from('knowledge')
          .update({
            ...dataToSave,
            updated_at: new Date().toISOString()
            // updated_by column doesn't exist in the database
          })
          .eq('id', faq.id)


        if (saveError) {
          console.error('[TROUBLESHOOTING] Save FAILED with error:', saveError)

          // Log more details about the error
          if (saveError.message?.includes('policy')) {
            console.error('[TROUBLESHOOTING] RLS POLICY VIOLATION - User lacks UPDATE permission')
            console.error('This is a database permission issue. User role:', user?.app_metadata?.role)
          }

          // Try alternative: use knowledge store method
          const { updateFAQ } = useKnowledgeStore.getState()
          await updateFAQ(faq.id, dataToSave as any)
        }

        // Add small delay to ensure DB write completes
        await new Promise(resolve => setTimeout(resolve, 100))

        // Always verify with SELECT to confirm update worked
        const { data: verifyData, error: verifyError } = await supabase
          .from('knowledge')
          .select('*')
          .eq('id', faq.id)
          .single()


        if (verifyError || !verifyData) {
          console.error('[TROUBLESHOOTING] CRITICAL: Update verification failed!')
          console.error('VerifyError:', verifyError)
          console.error('This means the UPDATE did not persist to the database')
          throw new Error(`Update nicht gespeichert! ${verifyError?.message || 'Datensatz nicht gefunden'}`)
        }

        // Check if our changes were actually applied
        const answerMatches = verifyData.answer === dataToSave.answer
        const statusMatches = verifyData.status === dataToSave.status


        if (!answerMatches || !statusMatches) {
          console.error('[TROUBLESHOOTING] WARNING: Data mismatch after update!')
          console.error('Expected:', { answer: dataToSave.answer?.substring(0, 50), status: dataToSave.status })
          console.error('Got:', { answer: verifyData.answer?.substring(0, 50), status: verifyData.status })
        }

        // Update form data with verified data from database
        // This ensures the form shows exactly what was saved
        setFormData({
          question: verifyData.question || '',
          answer: verifyData.answer || '',
          question_variations: verifyData.question_variations || [],
          category: verifyData.category || 'general',
          problem_type: verifyData.problem_type || '',
          tags: verifyData.tags || [],
          status: verifyData.status || 'draft',
          escalation_rule: verifyData.escalation_rule || 'self_solve',
          solution_content: verifyData.solution_content || {
            steps: [],
            images: [],
            links: []
          },
          followup_questions: verifyData.followup_question_ids || []
        })

        // Mark that we just saved to prevent useEffect from resetting formData
        justSavedRef.current = true
        formDataInitializedRef.current = true // Ensure it stays initialized

        // Archive business logic: mark import_sources as ignored
        if (dataToSave.status === 'archived' && faq.source_uid) {
          const { error: sourceError } = await supabase
            .from('import_sources')
            .update({
              status: 'ignored',
              updated_at: new Date().toISOString()
            })
            .eq('uid', faq.source_uid)

          if (sourceError) {
            console.error('[DEBUG] Error updating import_sources:', sourceError)
            // Don't throw - import_sources update is secondary
          }
        }

        toast({
          title: 'Erfolgreich',
          description: 'FAQ wurde aktualisiert'
        })
      } else {
        await createFAQ(dataToSave as any)
        toast({
          title: 'Erfolgreich',
          description: 'FAQ wurde erstellt'
        })
      }

      // IMPORTANT: Pass the FAQ with its ID to the callback
      const dataToPassToCallback = {
        ...dataToSave,
        id: faq?.id // Make sure ID is included!
      }

      if (onSave) {
        onSave(dataToPassToCallback)
      }

      // Reset hasEnhancedData after successful save
      setHasEnhancedData(false)

      // Don't reset other flags here - let the useEffect cleanup handle it
      // This way the flags stay active while the editor remains open
    } catch (error) {
      console.error('[DEBUG] Save failed:', error)
      toast({
        title: 'Fehler',
        description: 'FAQ konnte nicht gespeichert werden',
        variant: 'destructive'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const addVariation = () => {
    if (newVariation.trim()) {
      setFormData({
        ...formData,
        question_variations: [...formData.question_variations, newVariation.trim()]
      })
      setNewVariation('')
    }
  }

  const removeVariation = (index: number) => {
    setFormData({
      ...formData,
      question_variations: formData.question_variations.filter((_, i) => i !== index)
    })
  }

  const addTag = () => {
    if (newTag.trim()) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()]
      })
      setNewTag('')
    }
  }

  const removeTag = (index: number) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((_, i) => i !== index)
    })
  }

  const addStep = () => {
    if (newStep.trim()) {
      setFormData({
        ...formData,
        solution_content: {
          ...formData.solution_content,
          steps: [...(formData.solution_content?.steps || []), newStep.trim()]
        }
      })
      setNewStep('')
    }
  }

  const removeStep = (index: number) => {
    setFormData({
      ...formData,
      solution_content: {
        ...formData.solution_content,
        steps: (formData.solution_content?.steps || []).filter((_, i) => i !== index)
      }
    })
  }

  const addLink = () => {
    if (newLink.trim()) {
      setFormData({
        ...formData,
        solution_content: {
          ...formData.solution_content,
          links: [...(formData.solution_content?.links || []), newLink.trim()]
        }
      })
      setNewLink('')
    }
  }

  const removeLink = (index: number) => {
    setFormData({
      ...formData,
      solution_content: {
        ...formData.solution_content,
        links: (formData.solution_content?.links || []).filter((_, i) => i !== index)
      }
    })
  }

  const addFollowupQuestion = () => {
    if (newFollowupQuestion.trim()) {
      setFormData({
        ...formData,
        followup_questions: [...(formData.followup_questions || []), newFollowupQuestion.trim()]
      })
      setNewFollowupQuestion('')
    }
  }

  const removeFollowupQuestion = (index: number) => {
    setFormData({
      ...formData,
      followup_questions: (formData.followup_questions || []).filter((_, i) => i !== index)
    })
  }

  const updateStep = (index: number, value: string) => {
    const newSteps = [...(formData.solution_content?.steps || [])]
    newSteps[index] = value
    setFormData({
      ...formData,
      solution_content: {
        ...formData.solution_content,
        steps: newSteps
      }
    })
  }

  const handleEnhancementApply = async (enhanced: { question: string; answer: string; tags: string[]; question_variations?: string[] }) => {

    // Update form data
    const updatedData = {
      ...formData,
      question: enhanced.question,
      answer: enhanced.answer,
      tags: enhanced.tags,
      question_variations: enhanced.question_variations || formData.question_variations
    }

    setFormData(updatedData)
    setHasEnhancedData(true) // Mark that we have enhanced data - prevent formData reset
    setShowEnhancement(false)

    // Auto-save with re-embedding BUT do NOT close the editor
    if (faq?.id) {
      setIsSaving(true)
      try {
        // Only update content fields, preserve everything else
        const dataToSave = {
          question: enhanced.question,
          answer: enhanced.answer,
          tags: enhanced.tags,
          question_variations: enhanced.question_variations || [],
          updated_at: new Date().toISOString()
        }


        // Save DIRECTLY to Supabase, bypass store to avoid loadFAQs() race condition
        const { error: saveError } = await supabase
          .from('knowledge')
          .update(dataToSave as any)
          .eq('id', faq.id)

        if (saveError) {
          console.error('[DEBUG] Auto-save FAILED:', saveError)
          throw saveError
        }

        // Verify the saved data and update formData
        const { data: verifiedData, error: verifyError } = await supabase
          .from('knowledge')
          .select('*')
          .eq('id', faq.id)
          .single()

        if (!verifyError && verifiedData) {
          // Update form with verified data from database
          setFormData({
            question: verifiedData.question || '',
            answer: verifiedData.answer || '',
            question_variations: verifiedData.question_variations || [],
            category: verifiedData.category || 'general',
            problem_type: verifiedData.problem_type || '',
            tags: verifiedData.tags || [],
            status: verifiedData.status || 'draft',
            escalation_rule: verifiedData.escalation_rule || 'self_solve',
            solution_content: verifiedData.solution_content || {
              steps: [],
              images: [],
              links: []
            },
            followup_questions: verifiedData.followup_question_ids || []
          })
        }

        // Trigger re-embedding
        const response = await fetch('/api/knowledge/update-embedding', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ faqId: faq.id })
        })

        if (!response.ok) {
        }

        toast({
          title: '✅ Enhancement gespeichert',
          description: 'Änderungen wurden gespeichert. Wählen Sie einen Status und speichern Sie erneut.',
          duration: 4000
        })

        // DO NOT call onSave?.() - that would close the editor!
        // onSave?.(updatedData)  ❌ NOPE
      } catch (error) {
        console.error('[DEBUG] Auto-save failed:', error)
        toast({
          title: 'Fehler beim Speichern',
          description: 'Enhancement konnte nicht gespeichert werden',
          variant: 'destructive'
        })
      } finally {
        setIsSaving(false)
      }
    }
  }

  const handleFieldEnhancementApply = async (field: string, value: any) => {
    // Update form data for specific field
    const fieldUpdate: any = {}

    switch (field) {
      case 'question':
        fieldUpdate.question = value
        break
      case 'answer':
        fieldUpdate.answer = value
        break
      case 'tags':
        fieldUpdate.tags = value
        break
      case 'variations':
        fieldUpdate.question_variations = value
        break
    }

    const updatedData = { ...formData, ...fieldUpdate }
    setFormData(updatedData)

    // DO NOT auto-save - this function should only be called by EnhancementPreview
    // but we keep it for backward compatibility. The actual save happens via handleSubmit
  }

  // Show Enhancement Preview if enabled
  if (showEnhancement && faq?.id) {
    return (
      <EnhancementPreview
        faqId={faq.id}
        onApply={handleEnhancementApply}
        onFieldApply={handleFieldEnhancementApply}
        onCancel={() => setShowEnhancement(false)}
      />
    )
  }

  return (
    <Card className="w-full">
      <CardContent className="space-y-6 pt-6">
        {/* Escalation Rule - Defines Form Type */}
        <div className="border rounded-lg p-3 bg-muted/10">
          <div className="flex items-center gap-4">
            <Label htmlFor="escalation_rule" className="text-sm font-medium whitespace-nowrap">Typ:</Label>
            <Select
              value={formData.escalation_rule}
              onValueChange={(value: 'self_solve' | 'mission_solar' | 'neoom') => setFormData({ ...formData, escalation_rule: value })}
            >
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="self_solve">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Self-Solve
                  </div>
                </SelectItem>
                <SelectItem value="mission_solar">
                  <div className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Mission Solar
                  </div>
                </SelectItem>
                <SelectItem value="neoom">
                  <div className="flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Neoom
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-muted-foreground">
              {formData.escalation_rule === 'self_solve' && 'Lösung vorhanden'}
              {formData.escalation_rule === 'mission_solar' && 'Support nötig'}
              {formData.escalation_rule === 'neoom' && 'Technisch komplex'}
            </div>
          </div>
        </div>

        {/* Source Link & Embedding Status */}
        {faq?.id && (
          <div className="border rounded-lg p-3 bg-muted/10">
            <div className="flex items-center justify-between gap-4">
              {/* Source Link */}
              {sourceUrl && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium whitespace-nowrap">Quelle:</Label>
                  <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {sourceType === 'neoom' && 'Neoom Wiki'}
                    {sourceType === 'notion' && 'Notion Wiki'}
                    {sourceType === 'email' && 'Email'}
                    {!sourceType && 'Quelle öffnen'}
                    <span className="text-xs text-muted-foreground ml-1">
                      ({faq?.source_uid})
                    </span>
                  </a>
                </div>
              )}

              {/* Embedding Status */}
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium whitespace-nowrap">Vector Embedding:</Label>
                {!faq.vector_embedding ? (
                  <Badge variant="destructive" className="gap-1">
                    ❌ Kein Embedding
                  </Badge>
                ) : !faq.embedded_at || new Date(faq.updated_at) > new Date(faq.embedded_at) ? (
                  <Badge variant="outline" className="gap-1 border-yellow-500 text-yellow-700 bg-yellow-50">
                    ⚠️ Embedding veraltet
                  </Badge>
                ) : (
                  <Badge variant="default" className="gap-1 bg-green-600">
                    ✅ Aktuell
                  </Badge>
                )}
                {faq.embedded_at && (
                  <span className="text-xs text-muted-foreground">
                    ({new Date(faq.embedded_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })})
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Basic Information */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="question">Frage *</Label>
            <Input
              id="question"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              placeholder="Hauptfrage eingeben..."
              className="mt-1"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="answer">Antwort * (Markdown Editor mit Live-Vorschau)</Label>
              {faq?.id && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEnhancement(true)}
                  className="gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Aufwerten
                </Button>
              )}
            </div>
            <div className="relative overflow-hidden rounded-md border">
              <MarkdownEditor
                key={`answer-editor-${hasEnhancedData ? 'enhanced' : 'normal'}-${formData.answer?.substring(0, 10)}`}
                value={formData.answer}
                onChange={(value) => setFormData({ ...formData, answer: value })}
                height={400}
                preview="edit"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              💡 Markdown-Formatierung: **fett**, *kursiv*, # Überschrift, - Listen, [Link](url), `Code`, etc.
            </p>
          </div>
        </div>

        {/* Advanced AI-Generated Content */}
        <div className="border rounded-lg bg-muted/10">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between p-3 hover:bg-muted/20 transition-colors rounded-lg"
          >
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-missionSolar-primaryTeal" />
              <span className="font-medium">AI-generierte Inhalte</span>
              <Badge variant="outline" className="text-xs">
                Groq AI
              </Badge>
            </div>
            {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {showAdvanced && (
            <div className="p-3 space-y-4 border-t">
              {/* Category - Auto-generated */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Kategorie</Label>
                {formData.category ? (
                  <Badge variant="outline" className="px-3 py-1">
                    {formData.category === 'wallbox' ? 'Wallbox'
                      : formData.category === 'pv_system' ? 'PV System'
                      : formData.category === 'battery' ? 'Batterie'
                      : formData.category === 'app_access' ? 'App Zugang'
                      : formData.category === 'general' ? 'Allgemein'
                      : formData.category}
                  </Badge>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    Wird automatisch generiert
                  </p>
                )}
              </div>

              {/* Problem Type - Auto-generated */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Problemtyp</Label>
                {formData.problem_type ? (
                  <Badge variant="outline" className="px-3 py-1">
                    {formData.problem_type}
                  </Badge>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    Wird automatisch generiert
                  </p>
                )}
              </div>

              {/* Question Variations */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Fragevariationen</Label>
                {formData.question_variations.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {formData.question_variations.map((variation, index) => (
                      <Badge key={index} variant="secondary" className="px-2 py-1">
                        {variation}
                        <button
                          onClick={() => removeVariation(index)}
                          className="ml-2 hover:text-destructive"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    Werden automatisch generiert
                  </p>
                )}
              </div>

              {/* Solution Steps */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Lösungsschritte</Label>
                {(formData.solution_content?.steps || []).length > 0 ? (
                  <div className="space-y-2">
                    {(formData.solution_content?.steps || []).map((step, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <Badge className="mt-0.5" variant="outline">{index + 1}</Badge>
                        <div className="flex-1 text-sm">
                          <MarkdownRenderer content={step} />
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeStep(index)}
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    Werden automatisch generiert
                  </p>
                )}
              </div>

              {/* Links */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Hilfreiche Links</Label>
                {(formData.solution_content?.links || []).length > 0 ? (
                  <div className="space-y-1">
                    {(formData.solution_content?.links || []).map((link, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <a href={link} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline truncate flex-1">
                          {link}
                        </a>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeLink(index)}
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    Werden automatisch generiert
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Conditional Escalation Interface */}
        {formData.escalation_rule === 'self_solve' && (
          <div className="border rounded-lg p-4 bg-background">
            <Collapsible open={solutionStepsOpen} onOpenChange={setSolutionStepsOpen}>
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-base font-medium">Lösungsschritte (Manuell)</Label>
                    {solutionStepsOpen ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <Badge variant="default">Self-Solve</Badge>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Schritt-für-Schritt-Anleitung zur Problemlösung (Markdown unterstützt)
                </p>
            <div className="space-y-4">
              {(formData.solution_content?.steps || []).map((step, index) => (
                <div key={index} className="border rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-sm font-medium">Schritt {index + 1}</span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingStep(editingStep === index ? null : index)}
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeStep(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {editingStep === index ? (
                    <SimpleMarkdownEditor
                      value={step}
                      onChange={(value) => updateStep(index, value)}
                      placeholder="Schritt bearbeiten..."
                      height={150}
                    />
                  ) : (
                    <div className="prose prose-sm max-w-none">
                      <MarkdownRenderer content={step} />
                    </div>
                  )}
                </div>
              ))}
              <div className="border rounded-lg p-3">
                <Label className="text-sm font-medium mb-2 block">Neuen Schritt hinzufügen</Label>
                <SimpleMarkdownEditor
                  value={newStep}
                  onChange={setNewStep}
                  placeholder="Neuen Schritt eingeben... (Markdown unterstützt)"
                  height={150}
                />
                <Button
                  size="sm"
                  onClick={addStep}
                  className="mt-2"
                  disabled={!newStep.trim()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Schritt hinzufügen
                </Button>
              </div>
            </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {(formData.escalation_rule === 'mission_solar' || formData.escalation_rule === 'neoom') && (
          <div className="border rounded-lg p-4 bg-background">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base font-medium">Followup-Fragen für Eskalation</Label>
              <Badge variant={formData.escalation_rule === 'mission_solar' ? 'secondary' : 'outline'}>
                {formData.escalation_rule === 'mission_solar' ? 'Mission Solar Support' : 'Neoom Ticket'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {formData.escalation_rule === 'mission_solar'
                ? 'Fragen zur Klärung vager Kundenanfragen, die Mission Solar beantworten kann'
                : 'Fragen für technische Details, die nur Neoom lösen kann'}
            </p>
            <div className="space-y-2">
              {(formData.followup_questions || []).map((question, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="flex-1 p-2 bg-muted rounded-md">
                    <p className="text-sm">{question}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeFollowupQuestion(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  value={newFollowupQuestion}
                  onChange={(e) => setNewFollowupQuestion(e.target.value)}
                  placeholder="z.B. Welche Fehlermeldung wird angezeigt?"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFollowupQuestion())}
                  className="flex-1"
                />
                <Button size="sm" onClick={addFollowupQuestion} disabled={!newFollowupQuestion.trim()}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tags */}
        <div>
          <Label>Tags</Label>
          <p className="text-sm text-muted-foreground mb-2">
            Schlagwörter für bessere Kategorisierung
          </p>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.tags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="px-2 py-1">
                {tag}
                <button
                  onClick={() => removeTag(index)}
                  className="ml-2 hover:text-destructive"
                >
                  ×
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="Neues Tag hinzufügen..."
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              className="flex-1"
            />
            <Button size="sm" onClick={addTag}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

      </CardContent>

      <CardFooter className="flex justify-between">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            Abbrechen
          </Button>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleSubmit('draft')}
            disabled={isSaving}
          >
            <Save className="mr-2 h-4 w-4" />
            Als Entwurf speichern
          </Button>

          <Button
            variant="outline"
            onClick={() => handleSubmit('review')}
            disabled={isSaving}
          >
            <Send className="mr-2 h-4 w-4" />
            Zur Prüfung
          </Button>

          <Button
            onClick={() => handleSubmit('approved')}
            disabled={isSaving}
          >
            <CheckCircle className="mr-2 h-4 w-4" />
            Genehmigen & Speichern
          </Button>
        </div>
      </CardFooter>
    </Card>
  )
}